"use client";

import { Game, hasReleasedCompanion, localizePath } from "@repo/lib";
import { useI18n } from "@repo/ui/providers";
import { Badge, Card } from "@repo/ui/controls";
import Image from "next/image";
import Link from "next/link";
import { GamePreviewImage } from "./game-preview-image";

export function GameCard({ game }: { game: Game }) {
  const { t, locale } = useI18n();
  // Card-sized WebP (800x420, ~12-99 KB) straight from the edge instead of the
  // 1200x630 OG JPEG through /_next/image (scripts/generate-card-images.mjs).
  const previewImageUrl = game.web ? `${game.web}/card.webp` : null;
  const logoAlt = t("www.apps.logoAlt", { vars: { game: game.title } });

  return (
    <Link
      href={localizePath(`/apps/${game.id}`, locale)}
      className="block group"
      aria-label={t("www.apps.cardAria", { vars: { game: game.title } })}
    >
      <Card className="overflow-hidden hover:shadow-lg transition">
        {/* Preview Image with Overlapping Metadata */}
        <div className="relative aspect-1200/700 w-full bg-muted overflow-hidden">
          {previewImageUrl ? (
            <GamePreviewImage
              previewUrl={previewImageUrl}
              logo={game.logo}
              title={game.title}
              unoptimized
            />
          ) : (
            /* Fallback to logo-only when no preview */
            <div className="absolute inset-0 flex items-center justify-center">
              <Image
                src={game.logo}
                unoptimized
                alt={logoAlt}
                width={80}
                height={80}
                className="rounded"
                sizes="80px"
              />
            </div>
          )}

          {/* Overlapping Metadata with Blur */}
          <div className="absolute bottom-0 left-0 right-0 bg-background/80 backdrop-blur-md p-4 space-y-2">
            {/* Title with Icon */}
            <div className="flex items-center gap-2">
              <Image
                src={game.logo}
                unoptimized
                alt={logoAlt}
                width={32}
                height={32}
                className="rounded shrink-0"
                sizes="32px"
              />
              <h2 className="text-lg font-semibold truncate">{game.title}</h2>
            </div>

            {/* Platform Badges */}
            <div className="flex flex-wrap gap-1 text-xs">
              {hasReleasedCompanion(game) && (
                <Badge
                  variant="default"
                  className="hover:bg-primary pointer-events-none"
                >
                  {t("www.nav.companionApp")}
                </Badge>
              )}
              {game.overwolf && (
                <Badge
                  variant="outline"
                  className="border-primary/50 text-primary bg-primary/10 pointer-events-none"
                >
                  Overwolf
                </Badge>
              )}
              {game.web && (
                <Badge
                  variant="secondary"
                  className="hover:bg-secondary pointer-events-none"
                >
                  {t("www.apps.website")}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}
